import {ApolloError, FetchResult, gql, MutationTuple, useApolloClient, useMutation, useQuery} from '@apollo/client'
import {useAlert} from 'react-alert'
import {getGraphQLErrorMessage} from '~/util'
import {UpdateWeeklyEmailSettings, UpdateWeeklyEmailSettingsVariables} from './__types__/UpdateWeeklyEmailSettings'
import {WeeklyEmailRecipientMode} from '~/globalTypes'
import {WeeklyEmailSettings, WeeklyEmailSettingsVariables} from './__types__/WeeklyEmailSettings'

export const WEEKLY_EMAIL_SETTINGS = gql`
    query WeeklyEmailSettings($idStr: String!) {
        organisation(idStr: $idStr) {
            idStr
            weeklyEmailSettings {
                recipientMode
                featureEnabled
            }
        }
    }
`

export const UPDATE_WEEKLY_EMAIL_SETTINGS = gql`
    mutation UpdateWeeklyEmailSettings($weeklyEmailSettings: WeeklyEmailSettingsInput!, $organisationIdStr: String!) {
        updateWeeklyEmailSettings(weeklyEmailSettings: $weeklyEmailSettings, organisationIdStr: $organisationIdStr)
    }
`

export const useQueryWeeklyEmailSettings = (organisationIdStr: string) => {
    const query = useQuery<WeeklyEmailSettings, WeeklyEmailSettingsVariables>(WEEKLY_EMAIL_SETTINGS, {
        variables: {idStr: organisationIdStr},
    })
    return [query]
}

export const useMutationUpdateWeeklyEmailSettings = (): [
    MutationTuple<UpdateWeeklyEmailSettings, UpdateWeeklyEmailSettingsVariables>,
    (
        organisationIdStr: string,
        recipientMode?: WeeklyEmailRecipientMode | null,
        featureEnabled?: boolean | null,
    ) => Promise<FetchResult<UpdateWeeklyEmailSettings, Record<string, any>, Record<string, any>>>,
] => {
    const alert = useAlert()

    const client = useApolloClient()
    const mutation = useMutation<UpdateWeeklyEmailSettings, UpdateWeeklyEmailSettingsVariables>(
        UPDATE_WEEKLY_EMAIL_SETTINGS,
        {},
    )
    const callMutation = async (
        organisationIdStr: string,
        recipientMode?: WeeklyEmailRecipientMode | null,
        featureEnabled?: boolean | null,
    ) => {
        try {
            const result = await mutation[0]({
                variables: {organisationIdStr, weeklyEmailSettings: {recipientMode, featureEnabled}},
            })

            // Update the cached query result
            const existingData = client.readQuery<WeeklyEmailSettings, WeeklyEmailSettingsVariables>({
                query: WEEKLY_EMAIL_SETTINGS,
                variables: {idStr: organisationIdStr},
            })

            if (existingData?.organisation) {
                client.writeQuery<WeeklyEmailSettings, WeeklyEmailSettingsVariables>({
                    query: WEEKLY_EMAIL_SETTINGS,
                    variables: {idStr: organisationIdStr},
                    data: {
                        organisation: {
                            ...existingData.organisation,
                            weeklyEmailSettings: {
                                __typename: 'WeeklyEmailSettings',
                                recipientMode:
                                    recipientMode ??
                                    existingData.organisation.weeklyEmailSettings?.recipientMode ??
                                    null,
                                featureEnabled:
                                    featureEnabled ??
                                    existingData.organisation.weeklyEmailSettings?.featureEnabled ??
                                    false,
                            },
                        },
                    },
                })
            }

            alert.success(`Weekly email settings successfully updated.`)
            return result
        } catch (error) {
            alert.error(getGraphQLErrorMessage(error as ApolloError))
            throw error
        }
    }
    return [mutation, callMutation]
}
